/**
 * Phase A-2 — Pages sécurité administrateur.
 * 5 pages compactes exportées depuis ce fichier unique :
 *   SecurityAudit, SecurityEvents, SecuritySessions, SecurityIpRules, AdminSettings
 */
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  ScrollText, RefreshCw, AlertTriangle, Ban, CheckCircle2, Shield,
  Trash2, UserX, Settings as SettingsIcon, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  fetchAuditLogs, fetchSecurityEvents, resolveSecurityEvent,
  fetchSessions, revokeSession as apiRevokeSession,
  fetchIpRules, createIpRule, deleteIpRule,
  fetchSettings, patchSetting,
} from "@/lib/admin-api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");
const StatusBadge = ({ status }) => {
  if (!status) return <Badge variant="outline">—</Badge>;
  if (status >= 500) return <Badge className="bg-red-700 text-white">{status}</Badge>;
  if (status >= 400) return <Badge className="bg-amber-600 text-white">{status}</Badge>;
  return <Badge className="bg-emerald-600 text-white">{status}</Badge>;
};

/* ------------------------------------------------------------- */
/* SecurityAudit — table paginée + filtres                        */
/* ------------------------------------------------------------- */
export function SecurityAudit() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [filters, setFilters] = useState({ action: "", user_email: "", ip: "", critical_only: false });
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data, total } = await fetchAuditLogs({ page, limit, ...filters });
      setRows(data || []);
      setTotal(total || 0);
    } catch {
      toast.error("Impossible de charger le journal.");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  function exportCsv() {
    const header = ["date","user","action","method","path","ip","status","latency_ms","critical"];
    const csv = [header.join(",")]
      .concat(rows.map(r => [
        r.created_at, r.user_email || "", r.action, r.method || "", r.resource || "",
        r.ip || "", r.status || "", r.latency_ms || "", r.critical ? "yes" : "",
      ].map(x => String(x).includes(",") ? `"${x}"` : x).join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `audit-log-p${page}.csv`; a.click();
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4" data-testid="admin-audit-page">
      <div className="flex items-center gap-2">
        <ScrollText className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Journal d'audit</h1>
        <span className="ml-auto text-sm text-muted-foreground">{total.toLocaleString("fr-FR")} événements</span>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-5">
          <Input placeholder="action (ex. admin.)" value={filters.action}
            onChange={e => setFilters(f => ({...f, action: e.target.value}))}
            data-testid="audit-filter-action" />
          <Input placeholder="email utilisateur" value={filters.user_email}
            onChange={e => setFilters(f => ({...f, user_email: e.target.value}))}
            data-testid="audit-filter-email" />
          <Input placeholder="IP" value={filters.ip}
            onChange={e => setFilters(f => ({...f, ip: e.target.value}))}
            data-testid="audit-filter-ip" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={filters.critical_only}
              onChange={e => setFilters(f => ({...f, critical_only: e.target.checked}))}
              data-testid="audit-filter-critical" />
            Critique uniquement
          </label>
          <div className="flex gap-2">
            <Button onClick={() => { setPage(1); load(); }} disabled={loading} data-testid="audit-apply">
              <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Filtrer
            </Button>
            <Button variant="outline" onClick={exportCsv} data-testid="audit-export-csv">CSV</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase text-muted-foreground">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Utilisateur</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Méthode</th>
                <th className="px-3 py-2">Chemin</th>
                <th className="px-3 py-2">IP</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2">Latence</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={`border-b text-xs ${r.critical ? "bg-red-50/50 dark:bg-red-950/10" : ""}`}>
                  <td className="px-3 py-1 font-mono">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-1">{r.user_email || <span className="italic text-muted-foreground">anon</span>}</td>
                  <td className="px-3 py-1 font-mono">{r.critical && <span className="mr-1 text-red-600">●</span>}{r.action}</td>
                  <td className="px-3 py-1">{r.method || "—"}</td>
                  <td className="px-3 py-1 font-mono text-[10px]">{r.resource || "—"}</td>
                  <td className="px-3 py-1 font-mono">{r.ip || "—"}</td>
                  <td className="px-3 py-1"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-1 font-mono">{r.latency_ms ? `${Math.round(r.latency_ms)}ms` : "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">Aucun événement.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="sm" disabled={page<=1} onClick={() => setPage(p => p-1)} data-testid="audit-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm">Page {page} / {totalPages}</span>
        <Button variant="outline" size="sm" disabled={page>=totalPages} onClick={() => setPage(p => p+1)} data-testid="audit-next">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- */
/* SecurityEvents                                                 */
/* ------------------------------------------------------------- */
export function SecurityEvents() {
  const [rows, setRows] = useState([]);
  const [showResolved, setShowResolved] = useState(false);

  async function load() {
    try { setRows(await fetchSecurityEvents(!showResolved, 200)); }
    catch { toast.error("Chargement impossible."); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showResolved]);

  async function resolve(id) {
    try { await resolveSecurityEvent(id); toast.success("Marqué résolu."); load(); }
    catch { toast.error("Échec."); }
  }

  return (
    <div className="space-y-4" data-testid="admin-events-page">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Événements de sécurité</h1>
        <label className="ml-auto flex items-center gap-2 text-sm">
          <Switch checked={showResolved} onCheckedChange={setShowResolved} data-testid="events-show-resolved" />
          Inclure résolus
        </label>
      </div>
      <Card><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-[11px] uppercase text-muted-foreground">
            <th className="px-3 py-2">Date</th><th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">IP</th><th className="px-3 py-2">Détails</th>
            <th className="px-3 py-2">Statut</th><th className="px-3 py-2">Action</th>
          </tr></thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} className="border-b text-xs">
                <td className="px-3 py-1 font-mono">{fmtDate(e.created_at)}</td>
                <td className="px-3 py-1"><Badge variant="destructive">{e.event_type}</Badge></td>
                <td className="px-3 py-1 font-mono">{e.ip_address || "—"}</td>
                <td className="px-3 py-1 text-[10px]">{JSON.stringify(e.details).slice(0,80)}</td>
                <td className="px-3 py-1">{e.resolved
                  ? <Badge className="bg-emerald-600 text-white">Résolu</Badge>
                  : <Badge className="bg-red-600 text-white">Ouvert</Badge>}</td>
                <td className="px-3 py-1">
                  {!e.resolved && (
                    <Button size="sm" variant="outline" onClick={() => resolve(e.id)} data-testid={`event-resolve-${e.id}`}>
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Résoudre
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Aucun événement.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

/* ------------------------------------------------------------- */
/* SecuritySessions                                               */
/* ------------------------------------------------------------- */
export function SecuritySessions() {
  const [rows, setRows] = useState([]);
  async function load() {
    try { setRows(await fetchSessions()); }
    catch { toast.error("Erreur."); }
  }
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, []);

  async function revoke(id) {
    if (!confirm("Révoquer cette session ? L'utilisateur sera déconnecté.")) return;
    try { await apiRevokeSession(id); toast.success("Session révoquée."); load(); }
    catch { toast.error("Échec."); }
  }

  return (
    <div className="space-y-4" data-testid="admin-sessions-page">
      <div className="flex items-center gap-2">
        <Shield className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Sessions actives</h1>
        <span className="ml-auto text-sm text-muted-foreground">{rows.length} actives (refresh 15 s)</span>
      </div>
      <Card><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-[11px] uppercase text-muted-foreground">
            <th className="px-3 py-2">Utilisateur</th><th className="px-3 py-2">IP</th>
            <th className="px-3 py-2">Agent</th><th className="px-3 py-2">Dernière activité</th>
            <th className="px-3 py-2">Action</th>
          </tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b text-xs">
                <td className="px-3 py-1">{s.user_email || s.user_id || "—"}</td>
                <td className="px-3 py-1 font-mono">{s.ip_address || "—"}</td>
                <td className="px-3 py-1 text-[10px]">{(s.user_agent || "").slice(0,50)}</td>
                <td className="px-3 py-1 font-mono">{fmtDate(s.last_active)}</td>
                <td className="px-3 py-1">
                  <Button size="sm" variant="destructive" onClick={() => revoke(s.id)} data-testid={`session-revoke-${s.id}`}>
                    <UserX className="mr-1 h-3 w-3" /> Révoquer
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Aucune session active.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

/* ------------------------------------------------------------- */
/* SecurityIpRules                                                */
/* ------------------------------------------------------------- */
export function SecurityIpRules() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ ip_address: "", rule_type: "blacklist", reason: "", expires_in_hours: 24 });

  async function load() {
    try { setRows(await fetchIpRules()); }
    catch { toast.error("Erreur."); }
  }
  useEffect(() => { load(); }, []);

  async function add(e) {
    e.preventDefault();
    try {
      await createIpRule({
        ...form,
        expires_in_hours: form.expires_in_hours ? Number(form.expires_in_hours) : null,
      });
      toast.success("Règle ajoutée.");
      setForm({ ip_address: "", rule_type: "blacklist", reason: "", expires_in_hours: 24 });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
  }
  async function remove(id) {
    if (!confirm("Supprimer cette règle ?")) return;
    try { await deleteIpRule(id); toast.success("Supprimé."); load(); }
    catch { toast.error("Échec."); }
  }

  return (
    <div className="space-y-4" data-testid="admin-ip-page">
      <div className="flex items-center gap-2">
        <Ban className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Règles IP (whitelist / blacklist)</h1>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Ajouter une règle (super-admin)</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={add} className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <Input placeholder="IP ou CIDR (ex. 192.168.1.0/24)" value={form.ip_address}
              onChange={e => setForm(f => ({...f, ip_address: e.target.value}))} required
              data-testid="ip-form-address" />
            <select className="rounded border px-2 text-sm" value={form.rule_type}
              onChange={e => setForm(f => ({...f, rule_type: e.target.value}))}
              data-testid="ip-form-type">
              <option value="blacklist">blacklist</option>
              <option value="whitelist">whitelist</option>
            </select>
            <Input placeholder="Raison" value={form.reason}
              onChange={e => setForm(f => ({...f, reason: e.target.value}))}
              data-testid="ip-form-reason" />
            <Input type="number" placeholder="Expire (heures)" value={form.expires_in_hours}
              onChange={e => setForm(f => ({...f, expires_in_hours: e.target.value}))}
              data-testid="ip-form-expires" />
            <Button type="submit" data-testid="ip-form-submit">Ajouter</Button>
          </form>
        </CardContent>
      </Card>
      <Card><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-[11px] uppercase text-muted-foreground">
            <th className="px-3 py-2">IP</th><th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Raison</th><th className="px-3 py-2">Créé le</th>
            <th className="px-3 py-2">Expire</th><th className="px-3 py-2">Action</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b text-xs">
                <td className="px-3 py-1 font-mono">{r.ip_address}</td>
                <td className="px-3 py-1">
                  {r.rule_type === "blacklist"
                    ? <Badge className="bg-red-700 text-white">blacklist</Badge>
                    : <Badge className="bg-emerald-600 text-white">whitelist</Badge>}
                </td>
                <td className="px-3 py-1">{r.reason || "—"}</td>
                <td className="px-3 py-1 font-mono">{fmtDate(r.created_at)}</td>
                <td className="px-3 py-1 font-mono">{fmtDate(r.expires_at) || "∞"}</td>
                <td className="px-3 py-1">
                  <Button size="sm" variant="outline" onClick={() => remove(r.id)} data-testid={`ip-remove-${r.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Aucune règle.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

/* ------------------------------------------------------------- */
/* AdminSettings (+ mode maintenance)                             */
/* ------------------------------------------------------------- */
export function AdminSettings() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);

  async function load() {
    try { setRows(await fetchSettings()); }
    catch { toast.error("Erreur."); }
  }
  useEffect(() => { load(); }, []);

  async function save(key) {
    const val = key in edits ? edits[key] : rows.find(r => r.key === key)?.value;
    setSaving(key);
    try {
      await patchSetting(key, coerce(val));
      toast.success(`"${key}" mis à jour.`);
      setEdits(e => { const c = {...e}; delete c[key]; return c; });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
    finally { setSaving(null); }
  }
  function coerce(v) {
    if (typeof v === "boolean") return v;
    if (typeof v !== "string") return v;
    if (v === "true") return true;
    if (v === "false") return false;
    if (!isNaN(Number(v)) && v.trim() !== "") return Number(v);
    return v;
  }

  const maintenance = rows.find(r => r.key === "maintenance_mode");

  return (
    <div className="space-y-4" data-testid="admin-settings-page">
      <div className="flex items-center gap-2">
        <SettingsIcon className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Paramètres globaux</h1>
      </div>

      {maintenance && (
        <Card className={maintenance.value ? "border-red-500 bg-red-50 dark:bg-red-950/20" : ""}>
          <CardContent className="flex items-center justify-between gap-4 p-4">
            <div>
              <div className="font-bold">Mode maintenance</div>
              <div className="text-xs text-muted-foreground">
                Quand activé, toutes les routes /api sauf /admin retournent 503 avec le message personnalisé.
              </div>
            </div>
            <Switch
              checked={Boolean(maintenance.value)}
              onCheckedChange={async (checked) => {
                if (checked && !confirm("Activer le mode maintenance ? Les utilisateurs seront bloqués.")) return;
                await patchSetting("maintenance_mode", checked);
                toast.success(checked ? "Maintenance ACTIVÉE" : "Maintenance DÉSACTIVÉE");
                load();
              }}
              data-testid="maintenance-toggle"
            />
          </CardContent>
        </Card>
      )}

      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-[11px] uppercase text-muted-foreground">
            <th className="px-3 py-2">Clé</th><th className="px-3 py-2">Valeur</th>
            <th className="px-3 py-2">Description</th><th className="px-3 py-2 w-24">Action</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b">
                <td className="px-3 py-1.5 font-mono text-xs">{r.key}</td>
                <td className="px-3 py-1.5">
                  <Input className="h-8"
                    value={edits[r.key] !== undefined ? edits[r.key] : String(r.value)}
                    onChange={e => setEdits(x => ({...x, [r.key]: e.target.value}))}
                    data-testid={`setting-input-${r.key}`} />
                </td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.description}</td>
                <td className="px-3 py-1.5">
                  <Button size="sm" variant="outline" disabled={saving === r.key} onClick={() => save(r.key)}
                    data-testid={`setting-save-${r.key}`}>
                    {saving === r.key ? "…" : "Enregistrer"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
